import { MenuItem, Divider, ListSubheader } from '@mui/material';
import {
  flatMap,
  groupBy,
  lowerCase,
  sortBy,
  startCase,
  upperFirst,
} from 'lodash';
import {
  RHFMultiSelect,
  RHFMultiSelectProps,
  RHFSelect,
  RHFSelectProps,
} from 'src/components/hook-form';
import { useGetFeaturesQuery } from 'src/services/features';

type RHFSelectFeatureFieldProps = Omit<RHFSelectProps, 'children'> & {
  startsWith: string;
};

type RHFMultiSelectFeatureFieldProps = Omit<
  RHFMultiSelectProps,
  'options' | 'children'
> & {
  startsWith: string;
};

export function RHFSelectFeatureField({
  startsWith,
  ...rest
}: RHFSelectFeatureFieldProps) {
  const { data: features } = useGetFeaturesQuery();

  const filteredItems = groupBy(
    sortBy(
      features?.items.filter((f) => f.key.startsWith(startsWith)) ?? [],
      'name'
    ),
    (x) => x.propertyType.map((pt) => upperFirst(lowerCase(pt))).join(', ')
  );

  const hasGroups = Object.keys(filteredItems).length > 1;

  return (
    <RHFSelect {...rest}>
      <MenuItem value="" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        None
      </MenuItem>

      <Divider sx={{ borderStyle: 'dashed' }} />
      {flatMap(filteredItems, (items, group) => [
        hasGroups && <ListSubheader key={group}>{group}</ListSubheader>,
        ...items.map((item) => (
          <MenuItem key={item.key} value={item.key}>
            {item.name}
          </MenuItem>
        )),
      ])}
    </RHFSelect>
  );
}

export function RHFMultiSelectFeatureField({
  startsWith,
  ...rest
}: RHFMultiSelectFeatureFieldProps) {
  const { data: features } = useGetFeaturesQuery();

  const options = flatMap(
    groupBy(
      sortBy(
        features?.items.filter((f) => f.key.startsWith(startsWith)) ?? [],
        'name'
      ),
      'propertyType'
    ),
    (items, group) => [
      { value: group, label: startCase(lowerCase(group)), subHeader: true },
      ...items.map((item) => ({ value: item.key, label: item.name })),
    ]
  );

  return <RHFMultiSelect {...rest} options={options} />;
}
